<template>
  <div class="file-processor px-3">
    <!-- Área de carga de archivos - visible cuando no hay archivo o cuando hay archivo pero no está procesando -->
    <FileUploadArea 
      v-if="!file || (!loading && !processedData && !processingError)" 
      @file-selected="onFileSelected" 
      @create-without-fds="onCreateWithoutFDS"
      :opcionGuardar="opcionGuardar"
      :crearProductos="crearProductos"
      :hasExistingCache="hasExistingCache"
    />
    
    <!-- Progress tracker con información del archivo - visible durante el procesamiento -->
    <div v-if="loading" class="processing-container">
      <!-- Progress bar con toda la información -->
      <FileProgressTracker
        ref="progressTracker"
        :file="file"
        :loading="loading"
        :processingTitle="processingTitle"
        :processingDescription="processingDescription"
        @processing-cancelled="onProcessingCancelled"
        @processing-completed="onProcessingCompleted"
      />
    </div>
    
    <!-- Error del procesamiento -->
    <div v-if="processingError && !loading" class="processing-error">
      <div class="error-header d-flex align-items-center gap-2">
        <i class="fa fa-exclamation-triangle text-danger mr-2"></i>
        <h5>Error en el Procesamiento</h5>
      </div>
      <div class="error-content">
        <p class="text-muted">{{ processingError.message }}</p>
        <div class="error-actions d-flex justify-content-between align-items-center">
          <b-button variant="outline-danger" @click="resetProcessor">
            <i class="fa fa-arrow-left mr-1"></i>
            Volver a Cargar Archivo
          </b-button>
          <b-button variant="outline-light" class="text-dark" @click="retryProcessing">
            <i class="fa fa-refresh mr-1"></i>
            Reintentar
          </b-button>          
        </div>
      </div>
    </div>
  </div>
</template>

<script>

// Vuex
import { mapActions, mapMutations, mapState } from 'vuex'

// Componentes
import FileUploadArea from './FileUploadArea.vue'
import FileProgressTracker from './FileProgressTracker.vue'

// Helpers
import { isValidDocumentPdf } from '@/servicios/productos/ia/helpers/ProcessDataProductHelper';

export default {
  name: 'FileProcessor',
  
  components: {
    FileUploadArea,
    FileProgressTracker
  },

  props: {
    productId: {
      type: [String, Number],
      required: false
    },
    action: {
      type: String,
      default: 'CREATE'
    },
    opcionGuardar: {
      type: Boolean,
      required: false,
      default: false
    },
    crearProductos: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  
  data() {
    return {
      file: null,
      loading: false,
      processedData: null,
      processingTitle: "Analizando con Inteligencia Artificial",
      processingDescription: "Nuestra IA está procesando tu documento para extraer información precisa del producto.",
      processingError: null,
      hasExistingCache: false
    }
  },

  computed: {
    ...mapState('productos', ['productStateOptions'])
  },

  watch: {
    productId: {
      immediate: true,
      async handler(newId) {
        this.hasExistingCache = false;
        
        if (newId && this.action === 'UPDATE') {
          this.hasExistingCache = await this.checkExistingIaCache(newId);
        }
      }
    }
  },

  methods: {

    ...mapActions('productos', ['sendPdfSafetyDataSheet', 'checkExistingIaCache']),

    ...mapMutations('productos', ['updateSelectedProduct']),

    /**
     * Maneja la selección de archivo
     */
    onFileSelected(file) {
      this.file = file;
      this.processedData = null;
      this.processingError = null;
      
      // Iniciar procesamiento automáticamente
      this.processFile();
    },

    /**
     * Procesa el archivo seleccionado
     */
    async processFile() {
      
      if (!this.file) {
        return;
      }
      
      // Verificar que el store esté disponible
      if (!this.$store) {
        this.processingError = { message: 'Error: Store no disponible' };
        return;
      }
      
      // Verificar que la acción esté disponible
      if (!this.$store._actions['productos/sendPdfSafetyDataSheet']) {
        this.processingError = { message: 'Error: Acción de procesamiento no disponible' };
        return;
      }
      
      this.$set(this, 'loading', true);
      this.$set(this, 'processedData', null); // Limpiar datos anteriores
      this.$set(this, 'processingError', null); // Limpiar errores anteriores
      
      // Forzar actualización del DOM
      await this.$nextTick();
      
      const payload = {
        data: {
          file: this.file,
          action: this.action,
          productId: this.productId 
        },
        useGlobalLoading: false,
        callback: this.callbackProcessFile
      };
      
      await this.sendPdfSafetyDataSheet(payload);
    },

    /**
     * Callback del procesamiento de la API
     */
    callbackProcessFile(status, { response }) {
      if (!status) {
        this.$set(this, 'loading', false);
        this.$set(this, 'processingError', { message: response || 'Error en el Procesamiento' });
        this.$alertError(response || 'Error en el Procesamiento', 5000, false, 'Asistente IA · Crear Producto', 'info');
        this.$emit('file-processed', { success: false, data: null });
        return;
      }

      // Completar el progreso si el tracker está disponible
      if (this.$refs.progressTracker) {
        this.$refs.progressTracker.completeProgress();
      }
      
      // Procesar datos de la API
      this.processApiResponse(response);
    },

    /**
     * Procesa la respuesta de la API
     */
    processApiResponse(response) {    
      this.$set(this, 'loading', false);

      // revisar si los datos son validos como documento
      if (!isValidDocumentPdf(response)) {
        this.$set(this, 'processingError', { message: 'El documento no contiene la información necesaria para crear el producto. (Nombre, CAS y Fabricante, etc), asegúrate de que sea una FSD valida.' });
        this.$emit('file-processed', { success: false, data: null });
        return;
      }

      // Emitir evento de procesamiento completado exitoso
      this.$emit('file-processed', { 
        success: true, 
        data: response, 
        file: this.file
      });
    },

    /**
     * Maneja la cancelación del procesamiento
     */
    onProcessingCancelled() {
      this.$set(this, 'loading', false);
      this.$set(this, 'file', null);
      this.$set(this, 'processedData', null);
      this.$set(this, 'processingError', null);
    
      this.$emit('processing-cancelled');
    },

    /**
     * Maneja la finalización del procesamiento
     */
    onProcessingCompleted() {
      // El progreso se completó, pero esperamos la respuesta de la API
      // Esta función se llama cuando el progress tracker llega al 100%
    },

    /**
     * Obtiene el texto del estado
     */
    getEstadoText(estadoValue) {
      const estado = this.productStateOptions.find(opt => opt.value === estadoValue);
      return estado ? estado.text : 'No especificado';
    },

    /**
     * Resetea el estado del procesador
     */
    reset() {
      this.$set(this, 'file', null);
      this.$set(this, 'loading', false);
      this.$set(this, 'processedData', null);
      this.$set(this, 'processingError', null);
      
      // Emitir evento de reset
      this.$emit('processing-cancelled');
    },

    /**
     * Reintenta el procesamiento después de un error
     */
    retryProcessing() {
      this.processFile();
    },

    /**
     * Resetea el procesador para cargar un nuevo archivo
     */
    resetProcessor() {
      this.reset();
    },

    /**
     * Maneja la creación sin FDS
     */
    onCreateWithoutFDS() {
      this.$emit('create-without-fds');
    }
  }
}
</script>

<style scoped>
/* Estilos para el contenedor de procesamiento */
.processing-container {
  margin-top: 2rem;
}

/* Estilos existentes */
.processing-result {
  margin-top: 2rem;
  padding: 1.5rem;
  background: #f8f9fa;
  border-radius: 12px;
  border-left: 4px solid #28a745;
}

.processing-error {
  margin-top: 2rem;
  padding: 1.5rem;
  background: #f8f9fa;
  border-radius: 12px;
  border-left: 4px solid #dc3545;
}

.result-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.result-header i {
  font-size: 1.5rem;
}

.result-header h5 {
  margin: 0;
  color: #28a745;
  font-weight: 600;
}

.error-header h5 {
  margin: 0;
  color: #dc3545;
  font-weight: 600;
}

.result-content p {
  color: #6c757d;
  margin-bottom: 1rem;
}

.error-content p {
  margin-bottom: 1rem;
}

.result-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.result-list li {
  padding: 0.5rem 0;
  border-bottom: 1px solid #e9ecef;
}

.result-list li:last-child {
  border-bottom: none;
}

.result-list strong {
  color: #495057;
  margin-right: 0.5rem;
}

.error-actions {
  display: flex;
  gap: 10px;
  margin-top: 15px;
}
</style> 